import { rgbaToCss } from '../core/color';
import { sampleGradient, type Gradient } from '../core/gradient';
import type { Rect, RGBA } from '../core/types';
import { pathBounds, pathToPolylines } from './geometry';
import type { PathOp, ShapeStroke, Subpath } from './types';

/**
 * Vector art is rasterised by the 2D canvas rather than in GL: it brings correct
 * antialiasing, joins, caps and dashes for free, and a shape is only redrawn
 * when its geometry changes, so this never sits in the per-frame path.
 */
function createContext(width: number, height: number): CanvasRenderingContext2D | null {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  return canvas.getContext('2d', { willReadFrequently: true });
}

/** Canvas composite operation that implements each path operation. */
const OP_COMPOSITE: Record<PathOp, GlobalCompositeOperation> = {
  add: 'source-over',
  subtract: 'destination-out',
  intersect: 'destination-in',
  exclude: 'xor',
};

function traceSubpath(ctx: CanvasRenderingContext2D, points: readonly { x: number; y: number }[], closed: boolean): void {
  if (points.length === 0) return;
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length; i += 1) ctx.lineTo(points[i].x, points[i].y);
  if (closed) ctx.closePath();
}

/**
 * Draws the shape's silhouette in white on transparent. Every subpath is applied
 * in order with its own operation, which is what makes "subtract front shape"
 * and friends behave the way they do in Photoshop.
 */
function drawSilhouette(
  ctx: CanvasRenderingContext2D,
  subpaths: readonly Subpath[],
  width: number,
  height: number,
): void {
  const polylines = pathToPolylines(subpaths);
  ctx.clearRect(0, 0, width, height);
  ctx.fillStyle = '#fff';

  for (let i = 0; i < subpaths.length; i += 1) {
    const subpath = subpaths[i];
    if (!subpath.closed || polylines[i].length < 3) continue;
    // The first subpath has nothing to combine with, so it always just draws.
    ctx.globalCompositeOperation = i === 0 ? 'source-over' : OP_COMPOSITE[subpath.op];
    traceSubpath(ctx, polylines[i], true);
    ctx.fill('evenodd');
  }
  ctx.globalCompositeOperation = 'source-over';
}

/** Coverage of the filled shape, for vector masks and Path to Selection. */
export function rasterizeCoverage(
  subpaths: readonly Subpath[],
  width: number,
  height: number,
): Uint8Array | null {
  if (width <= 0 || height <= 0) return null;
  const ctx = createContext(width, height);
  if (!ctx) return null;

  drawSilhouette(ctx, subpaths, width, height);
  const rgba = ctx.getImageData(0, 0, width, height).data;
  const coverage = new Uint8Array(width * height);
  for (let i = 0; i < coverage.length; i += 1) coverage[i] = rgba[i * 4 + 3];
  return coverage;
}

/** Fill of a shape as the rasteriser wants it: a flat colour or a gradient. */
export type FillStyle =
  | { kind: 'solid'; color: RGBA }
  | { kind: 'gradient'; gradient: Gradient; angle: number };

export interface ShapePaintStyle {
  fill: FillStyle | null;
  stroke: (ShapeStroke & { color: RGBA }) | null;
}

/**
 * Builds the canvas paint for a fill. A gradient runs across the shape's own
 * bounds at `angle`, sampled into stops so the model's separate colour and
 * opacity stops end up in a single canvas gradient.
 */
function fillPaint(
  ctx: CanvasRenderingContext2D,
  fill: FillStyle,
  bounds: Rect | null,
): string | CanvasGradient {
  if (fill.kind === 'solid') return rgbaToCss(fill.color);

  const box = bounds ?? { x: 0, y: 0, width: ctx.canvas.width, height: ctx.canvas.height };
  const cx = box.x + box.width / 2;
  const cy = box.y + box.height / 2;
  const dx = Math.cos(fill.angle);
  const dy = Math.sin(fill.angle);
  // Half the box projected on the gradient direction: the span that keeps the
  // ends of the ramp exactly at the edges of the shape.
  const half = (Math.abs(dx) * box.width + Math.abs(dy) * box.height) / 2;
  const paint = ctx.createLinearGradient(cx - dx * half, cy - dy * half, cx + dx * half, cy + dy * half);
  for (let i = 0; i <= GRADIENT_STOPS; i += 1) {
    const t = i / GRADIENT_STOPS;
    paint.addColorStop(t, rgbaToCss(sampleGradient(fill.gradient, t)));
  }
  return paint;
}

/** Stops used to approximate a gradient's ramp on the canvas. */
const GRADIENT_STOPS = 32;

function applyStrokeStyle(ctx: CanvasRenderingContext2D, stroke: ShapeStroke & { color: RGBA }): void {
  ctx.strokeStyle = rgbaToCss(stroke.color);
  ctx.lineWidth = stroke.width;
  ctx.lineJoin = stroke.join;
  ctx.lineCap = stroke.cap;
  ctx.setLineDash(stroke.dash);
}

/**
 * Full RGBA pixels of a shape layer: the fill clipped to the silhouette, then
 * the stroke on top. An inside or outside stroke is drawn at twice the width and
 * clipped to the shape, the standard trick for alignment the canvas lacks.
 */
export function rasterizeShape(
  subpaths: readonly Subpath[],
  width: number,
  height: number,
  style: ShapePaintStyle,
): ImageData | null {
  if (width <= 0 || height <= 0) return null;
  const ctx = createContext(width, height);
  if (!ctx) return null;

  if (style.fill) {
    drawSilhouette(ctx, subpaths, width, height);
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = fillPaint(ctx, style.fill, pathBounds(subpaths));
    ctx.fillRect(0, 0, width, height);
    ctx.globalCompositeOperation = 'source-over';
  } else {
    ctx.clearRect(0, 0, width, height);
  }

  const stroke = style.stroke;
  if (stroke && stroke.width > 0) {
    const polylines = pathToPolylines(subpaths);
    const strokeCtx = createContext(width, height);
    if (!strokeCtx) return null;

    applyStrokeStyle(strokeCtx, stroke);
    // An off-centre stroke is drawn at double width and then cut against the
    // silhouette, so exactly the half on the wanted side survives.
    if (stroke.align !== 'center') strokeCtx.lineWidth = stroke.width * 2;
    for (let i = 0; i < subpaths.length; i += 1) {
      if (polylines[i].length < 2) continue;
      traceSubpath(strokeCtx, polylines[i], subpaths[i].closed);
      strokeCtx.stroke();
    }

    if (stroke.align !== 'center') {
      const maskCtx = createContext(width, height);
      if (!maskCtx) return null;
      drawSilhouette(maskCtx, subpaths, width, height);
      strokeCtx.globalCompositeOperation =
        stroke.align === 'inside' ? 'destination-in' : 'destination-out';
      strokeCtx.drawImage(maskCtx.canvas, 0, 0);
      strokeCtx.globalCompositeOperation = 'source-over';
    }

    ctx.drawImage(strokeCtx.canvas, 0, 0);
  }

  return ctx.getImageData(0, 0, width, height);
}
